import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type { CursorQuery, ModerateInput, ReportQueueQuery } from '@onov/contracts';
import { api } from './api.js';

/** Query keys are namespaced so a mutation can invalidate exactly its surface. */
export const keys = {
  dashboard: ['dashboard'] as const,
  reports: (q: Partial<ReportQueueQuery>) => ['reports', q] as const,
  appeals: (q: Partial<CursorQuery>) => ['appeals', q] as const,
  upgrades: (q: Partial<CursorQuery>) => ['upgrades', q] as const,
  members: (q: string, cursor?: string) => ['members', q, cursor] as const,
  member: (id: string) => ['member', id] as const,
  memberActivity: (id: string) => ['member', id, 'activity'] as const,
  reportContext: (id: string) => ['report', id, 'context'] as const,
  sanction: (id: string) => ['sanction', id] as const,
  payments: (q: Record<string, unknown>) => ['payments', q] as const,
  tickets: (q: Record<string, unknown>) => ['tickets', q] as const,
  audit: (q: Record<string, unknown>) => ['audit', q] as const,
  admins: ['admins'] as const,
  roles: ['roles'] as const,
};

/** Queues refetch on focus and every 30s — stale data means two moderators acting on one item. */
const queueOpts = {
  staleTime: 10_000,
  refetchInterval: 30_000,
  placeholderData: keepPreviousData,
} as const;

export const useDashboard = () =>
  useQuery({
    queryKey: keys.dashboard,
    queryFn: () => api.admin.dashboard(),
    refetchInterval: 60_000,
  });
export const useReports = (q: Partial<ReportQueueQuery>) =>
  useQuery({
    queryKey: keys.reports(q),
    queryFn: () => api.admin.reports({ query: q }),
    ...queueOpts,
  });
export const useAppeals = (q: Partial<CursorQuery>) =>
  useQuery({
    queryKey: keys.appeals(q),
    queryFn: () => api.admin.appeals({ query: q }),
    ...queueOpts,
  });
export const useUpgrades = (q: Partial<CursorQuery>) =>
  useQuery({
    queryKey: keys.upgrades(q),
    queryFn: () => api.admin.upgradeQueue({ query: q }),
    ...queueOpts,
  });
export const useMembers = (q: string, cursor?: string) =>
  useQuery({
    queryKey: keys.members(q, cursor),
    queryFn: () => api.admin.members({ query: { q, cursor } }),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
export const useMember = (id: string) =>
  useQuery({
    queryKey: keys.member(id),
    queryFn: () => api.admin.member({ params: { userId: id } }),
  });
export const useMemberActivity = (id: string) =>
  useQuery({
    queryKey: keys.memberActivity(id),
    queryFn: () => api.admin.memberActivity({ params: { userId: id } }),
    enabled: !!id,
  });
export const useReportContext = (id: string) =>
  useQuery({
    queryKey: keys.reportContext(id),
    queryFn: () => api.admin.reportContext({ params: { reportId: id } }),
    enabled: !!id,
  });
/** Audited data export (FR-ADM-44) — the browser saves the JSON it gets back. */
export const useMemberExport = () =>
  useMutation({
    mutationFn: (v: { userId: string; reason: string }) =>
      api.admin.memberExport({ params: { userId: v.userId }, body: { reason: v.reason } }),
  });
export const useSanction = (id: string) =>
  useQuery({
    queryKey: keys.sanction(id),
    queryFn: () => api.admin.sanction({ params: { userId: id } }),
  });
export const usePayments = (q: Record<string, unknown>) =>
  useQuery({
    queryKey: keys.payments(q),
    queryFn: () => api.admin.payments({ query: q }),
    placeholderData: keepPreviousData,
  });
export const useTickets = (q: Record<string, unknown>) =>
  useQuery({
    queryKey: keys.tickets(q),
    queryFn: () => api.admin.tickets({ query: q }),
    ...queueOpts,
  });
export const useAudit = (q: Record<string, unknown>) =>
  useQuery({
    queryKey: keys.audit(q),
    queryFn: () => api.admin.audit({ query: q }),
    placeholderData: keepPreviousData,
  });
export const useAdmins = () =>
  useQuery({ queryKey: keys.admins, queryFn: () => api.admin.admins() });

/** Mutations invalidate their queue AND the dashboard counters. */
function useInvalidating<TArgs, TResult>(fn: (a: TArgs) => Promise<TResult>, prefixes: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const p of [...prefixes, 'dashboard']) void qc.invalidateQueries({ queryKey: [p] });
    },
  });
}
export const useClaimReport = () =>
  useInvalidating(
    (reportId: string) => api.admin.claimReport({ params: { reportId } }),
    ['reports'],
  );
export const useModerate = () =>
  useInvalidating(
    (a: { reportId: string; body: ModerateInput }) =>
      api.admin.moderate({ params: { reportId: a.reportId }, body: a.body }),
    ['reports', 'sanction', 'member'],
  );
export const useDecideAppeal = () =>
  useInvalidating(
    (a: { appealId: string; overturn: boolean; reason: string }) =>
      api.admin.decideAppeal({
        params: { appealId: a.appealId },
        body: { overturn: a.overturn, reason: a.reason },
      }),
    ['appeals', 'sanction'],
  );
export const useDecideUpgrade = () =>
  useInvalidating(
    (a: { requestId: string; approve: boolean; reason: string }) =>
      api.admin.decideUpgrade({
        params: { requestId: a.requestId },
        body: { approve: a.approve, reason: a.reason },
      }),
    ['upgrades'],
  );
export const useSuspend = () =>
  useInvalidating(
    (a: { userId: string; days: number; reason: string }) =>
      api.admin.suspendMember({
        params: { userId: a.userId },
        body: { days: a.days, reason: a.reason },
      }),
    ['sanction', 'member'],
  );
export const useReinstate = () =>
  useInvalidating(
    (a: { userId: string; reason: string }) =>
      api.admin.reinstateMember({ params: { userId: a.userId }, body: { reason: a.reason } }),
    ['sanction', 'member'],
  );
export const useRefund = () =>
  useInvalidating(
    (a: { paymentId: string; amountMinor?: number; reason: string }) =>
      api.admin.refund({
        params: { paymentId: a.paymentId },
        body: { amountMinor: a.amountMinor, reason: a.reason },
      }),
    ['payments'],
  );
export const useTicketReply = () =>
  useInvalidating(
    (a: { ticketId: string; body: string; status?: string; reason: string }) =>
      api.admin.ticketReply({
        params: { ticketId: a.ticketId },
        body: { body: a.body, status: a.status as never, reason: a.reason },
      }),
    ['tickets'],
  );
export const useTierConfig = () =>
  useInvalidating(
    (a: { tier: string; supporterThreshold?: number; reason: string }) =>
      api.admin.tierConfig({
        params: { tier: a.tier },
        body: { supporterThreshold: a.supporterThreshold, reason: a.reason },
      }),
    ['upgrades'],
  );
export const useGrantTier = () =>
  useInvalidating(
    (a: { userId: string; tier: string; reason: string }) =>
      api.admin.grantTier({
        params: { userId: a.userId },
        body: { tier: a.tier as never, reason: a.reason },
      }),
    ['member'],
  );
export const useCreateAdmin = () =>
  useInvalidating(
    (a: { email: string; password: string; role: string; reason: string }) =>
      api.admin.createAdmin({ body: a as never }),
    ['admins'],
  );
export const useSuspendAdmin = () =>
  useInvalidating(
    (a: { adminId: string; reason: string }) =>
      api.admin.suspendAdmin({ params: { adminId: a.adminId }, body: { reason: a.reason } }),
    ['admins'],
  );
export const useSetAdminRole = () =>
  useInvalidating(
    (a: { adminId: string; role?: string; customRoleId?: string; reason: string }) =>
      api.admin.setAdminRole({
        params: { adminId: a.adminId },
        body: { role: a.role, customRoleId: a.customRoleId, reason: a.reason } as never,
      }),
    ['admins', 'roles'],
  );
export const useRoles = () => useQuery({ queryKey: keys.roles, queryFn: () => api.admin.roles() });
export const useCreateRole = () =>
  useInvalidating(
    (a: { name: string; permissions: string[]; reason: string }) =>
      api.admin.createRole({ body: a as never }),
    ['roles'],
  );
export const useUpdateRole = () =>
  useInvalidating(
    (a: { roleId: string; name: string; permissions: string[]; reason: string }) =>
      api.admin.updateRole({
        params: { roleId: a.roleId },
        body: { name: a.name, permissions: a.permissions, reason: a.reason } as never,
      }),
    ['roles', 'admins'],
  );
export const useDeleteRole = () =>
  useInvalidating(
    (a: { roleId: string; reason: string }) =>
      api.admin.deleteRole({ params: { roleId: a.roleId }, body: { reason: a.reason } }),
    ['roles'],
  );
export const usePublishPolicy = () =>
  useMutation({
    mutationFn: (a: {
      kind: string;
      title: string;
      bodyMarkdown: string;
      effectiveAt: Date;
      reason: string;
    }) => api.admin.publishPolicy({ body: a as never }),
  });
export const useUpsertFaq = () =>
  useMutation({
    mutationFn: (a: Record<string, unknown>) => api.admin.upsertFaq({ body: a as never }),
  });
export const useUpsertTemplate = () =>
  useMutation({
    mutationFn: (a: Record<string, unknown>) => api.admin.upsertTemplate({ body: a as never }),
  });
export const useBroadcastPreview = () =>
  useMutation({
    mutationFn: (a: Record<string, unknown>) => api.admin.broadcastPreview({ body: a as never }),
  });
export const useBroadcast = () =>
  useMutation({
    mutationFn: (a: Record<string, unknown>) => api.admin.broadcast({ body: a as never }),
  });

export type ReportRow = z.infer<(typeof adminRoutes)['reports']['response']>['items'][number];
import type { adminRoutes } from '@onov/contracts';

/** Runtime categories are shared with the member app, including historical filters. */
export function useCategories() {
  return useQuery({
    queryKey: ['taxonomy', 'categories'],
    queryFn: () => api.taxonomy.categories({ query: { includeDisabled: true } }),
    staleTime: 30_000,
  });
}
